import { SqlValue } from '@sqlite.org/sqlite-wasm'
import { isArray } from 'lodash'

import { DataType, cacheKeyForRecordType } from '../types/DataType'
import { RecordType } from '../types/RecordType'
import { ISerializable } from './ISerializable'

export type WorkerMessageType =
    | 'exec'
    | 'initialize'
    | 'drop'
    | 'create'
    | 'start_transaction'
    | 'end_transaction'
    | 'rollback_transaction'

export type WorkerMessage = {
    type: WorkerMessageType
    payload?: SQLWorkerEnvelope
    uuid: string
    transactionCorrelationId?: string
}

export type PayloadType = {
    type: DataType
    data: any
}

export type WorkerResponseMessage = {
    type: WorkerMessageType | 'error' | 'log'
    payload: any[]
    cacheRows?: PayloadType[]
    metaData?: any // todo define time for this
    uuid?: string
    error: any
}

export type FKType = {
    // use the table name as the key and the model properties as the value (columns to extract)
    // ex. {user: {id: 'user-id-1', owner: 'user-id-2', 'user name: 'user_name' .... }}
    // setting users: ['owner'] will ensure the cache will return the user with the id 'user-id-2' when the owner column is read
    // use column mappings to extract the correct column from the object, defaults to string / string[] parsing if column mappings is not set
    adjustments?: string[]
    aliases?: string[]
    balances?: string[]
    blocks?: string[]
    bookings?: string[]
    campaigns?: string[]
    categories?: string[]
    changes?: string[]
    channels?: string[]
    charges?: string[]
    clients?: string[]
    components?: string[]
    conferences?: string[]
    contacts?: string[]
    contracts?: string[]
    credentials?: string[]
    criterias?: string[]
    dashboards?: string[]
    deposits?: string[]
    devices?: string[]
    estimates?: string[]
    events?: string[]
    feeds?: string[]
    fields?: string[]
    features?: string[]
    gifts?: string[]
    goals?: string[]
    images?: string[]
    invoices?: string[]
    items?: string[]
    industries?: string[]
    jobs?: string[]
    lessons?: string[]
    links?: string[]
    lines?: string[]
    locations?: string[]
    messages?: string[]
    notes?: string[]
    numbers?: string[]
    orders?: string[]
    packages?: string[]
    payees?: string[]
    payments?: string[]
    posts?: string[]
    promotions?: string[]
    readers?: string[]
    records?: string[]
    reports?: string[]
    reviews?: string[]
    resources?: string[]
    requirements?: string[]
    royalties?: string[]
    schedules?: string[]
    signatures?: string[]
    sources?: string[]
    subscribers?: string[]
    subaccounts?: string[]
    subscriptions?: string[]
    tasks?: string[]
    teams?: string[]
    templates?: string[]
    tcrs?: string[]
    tests?: string[]
    triggers?: string[]
    taxcodes?: string[]
    types?: string[]
    users?: string[]
    // dynamic table mapping used when the table name is not known (ex. Line.trandoc)
    dynamic?: string[]

    // objects are always in the cache
    // editions?: string[];
    // industries?: string[];
    // taxcodes?: string[];

    // when mapping a column that is an object opposed to a string / string[], we need to have a way for the fk parser to read the object
    // ex. when a team is returned from the api it looks like this
    // { team: { id: 'team-id-1', name: 'team name', ..., members: [{ person: 'user-id-1', team: 'team-id-1', cancelled_date: null  }, { person: 'user-id-2', team: 'team-id-1', cancelled_date: null }]  } }
    // because members is an array of objects we need to read the members array and extract the person id
    columnExtractors?: {
        [column: string]: string
    }
}

export type SQLWorkerEnvelope = SQLBuilderType & {
    // used to send sql data to the worker
    sql?: string // raw sql override
}

type SQLBuilderType = {
    // internal type used to store sql data
    columns: string[]
    tables: string[]
    joins: string[][]
    binds: SqlValue[]
    fieldBinds: any[]
    filters: string[]
    orderBy: string[]
    unions: SQLBuilder[]
    fk?: FKType
}

export class SQLBuilder implements ISerializable {
    readonly iSerializable = true

    private data: SQLBuilderType

    constructor(data?: Partial<SQLBuilderType>) {
        // todo lets not use SQLWorkerEnvelope here to match mobile, need to refactor everywhere
        this.data = {
            columns: [],
            tables: [],
            joins: [],
            binds: [],
            fieldBinds: [],
            filters: [],
            orderBy: [],
            ...data,
            unions: (data?.unions || []).map((sql) => new SQLBuilder(sql.data)),
        }
    }

    serialize() {
        return this.data
    }

    deserialize(obj: any): void {
        this.data = obj
    }

    json() {
        return this.data
    }

    clone() {
        const copy = new SQLBuilder()
        copy.data.columns = [...this.data.columns]
        copy.data.tables = [...this.data.tables]
        copy.data.joins = [...this.data.joins]
        copy.data.filters = [...this.data.filters]
        copy.data.binds = [...this.data.binds]
        copy.data.fieldBinds = [...this.data.fieldBinds]
        copy.data.unions = [...this.data.unions]
        copy.data.orderBy = [...this.data.orderBy]
        return copy
    }

    addField(column: string, alias?: string, ...binds: (SqlValue | boolean)[]) {
        const formattedColumn = !!alias ? `${column} ${alias}` : column
        this.data.columns.push(formattedColumn)
        this.data.fieldBinds.push(...binds.map((z) => (typeof z === 'boolean' ? (z ? 1 : 0) : z)))
    }

    addTable(table: string, join?: string) {
        this.data.tables.push(table)
        if (!!join) {
            this.data.joins.push([table, join])
        }
    }

    filterBy(filter: string, ...binds: (SqlValue | boolean)[]) {
        this.data.filters.push(filter)
        this.data.binds.push(...binds.map((z) => (typeof z === 'boolean' ? (z ? 1 : 0) : z)))
    }

    filterByWithArgs(filter: string, binds: (SqlValue | boolean)[]) {
        this.data.filters.push(filter)
        this.data.binds.push(...binds.map((z) => (typeof z === 'boolean' ? (z ? 1 : 0) : z)))
    }

    orderBy(column: string, descending = false) {
        const order = descending ? 'DESC' : 'ASC'
        this.data.orderBy.push(`${column} ${order}`)
    }

    addUnion(sql: SQLBuilder) {
        this.data.unions.push(sql)
    }

    allBinds(): SqlValue[] {
        return [
            ...this.data.fieldBinds,
            ...this.data.binds,
            ...this.data.unions.flatMap((sql: SQLBuilder) => sql.allBinds()),
        ].map((z) => (typeof z === 'boolean' ? (z ? 1 : 0) : z))
    }

    set fk(fk: FKType | undefined) {
        this.data.fk = fk
    }

    sql() {
        if (
            this.data.tables.length &&
            !this.data.columns.includes(`'${this.data.tables[0]}' as table_name`)
        )
            this.data.columns.push(`'${this.data.tables[0]}' as table_name`) // always use primary join table name

        let sql = 'SELECT '
        sql += this.data.columns.length > 0 ? this.data.columns.join(', ') : '*'

        sql += this.data.tables.length > 0 ? ` FROM ${this.data.tables[0]}` : ''

        if (this.data.joins.length > 0) {
            sql += this.data.joins.map((join) => ` JOIN ${join[0]} ON ${join[1]}`).join(' ')
        }

        sql += this.data.filters.length > 0 ? ` WHERE ${this.data.filters.join(' AND ')}` : ''

        if (this.data.unions.length > 0) {
            sql += this.data.unions
                .map((unionSql) => {
                    // Clear orderBy for unions
                    unionSql.data.orderBy = []
                    return ` UNION ${unionSql.sql().replace(';', '')}`
                })
                .join(' ')
        }

        sql += this.data.orderBy.length > 0 ? ` ORDER BY ${this.data.orderBy.join(', ')};` : ';'

        return sql
    }

    /**
     * Builds a sql query to get the forign key data for the given rows
     *
     * @param rows the main sql result rows, used to extract column data based on the fk
     * @param idToTableName a mapping of dynamic ids to their table
     * @returns the sql query to get the fk data
     * */
    fkSql(rows: any[], idToTableName: { [id: string]: DataType }): SQLBuilder | undefined {
        const fk = this.data.fk
        if (!fk || !rows?.length) {
            return
        }

        let tableBindings = SQLBuilder.addFkBindings(fk, rows)
        if ('dynamic' in tableBindings) {
            tableBindings = SQLBuilder.addDynamicBinds(tableBindings, idToTableName)
            delete tableBindings['dynamic']
        }

        // use the table map to id list to build a sql query
        let sql: SQLBuilder | undefined = undefined
        for (const table in tableBindings) {
            const ids = tableBindings[table]
            if (ids.length) {
                const tableSql = new SQLBuilder({
                    columns: ['jsonData', `'${table}' as table_name`],
                    tables: [table],
                    filters: [
                        `${cacheKeyForRecordType(table as DataType)} in (${ids.map(() => '?').join(', ')})`,
                    ],
                    binds: ids,
                })
                if (!sql) {
                    sql = tableSql
                }
                else {
                    sql.addUnion(tableSql)
                }
            }
        }
        return sql
    }

    /**
     * Builds a sql query to get the table name for the given columns matching 'dynamic' in the fk
     *
     * @param rows the main sql result rows, used to extract column data based on the fk 'dynamic' columns
     * @returns the sql query to get the table types using the dynamic ids
     * */
    dynamicTypeSql(rows: any[]): SQLBuilder | undefined {
        const fk = this.data.fk
        if (!fk || !rows?.length || !('dynamic' in fk)) {
            return
        }

        let binds: SqlValue[] = []
        for (const row of rows) {
            const jsonDataObj = !!row && 'jsonData' in row ? JSON.parse(row['jsonData']) : undefined
            if (!jsonDataObj) continue

            const columns: string[] = fk['dynamic']!
            binds = SQLBuilder.fkColumnsToBinding(columns, jsonDataObj, binds, fk.columnExtractors)
        }

        return binds.length
            ? new SQLBuilder({
                  columns: ['id', 'type'],
                  tables: ['keys'],
                  filters: [`id in (${binds.map(() => '?').join(', ')})`],
                  binds,
              })
            : undefined
    }

    /**
     * For the given columns, extract the data from the jsonDataObj and add it to the binds
     *
     * @param columns columns to extract from the jsonDataObj, columns must all be in the same table
     * @param jsonDataObj the json data object to extract the columns from
     * @param binds the binds to add the extracted data to
     * @returns
     */
    private static fkColumnsToBinding(
        columns: string[],
        jsonDataObj: any,
        binds: SqlValue[],
        objectMaps?: { [id: string]: string }
    ) {
        for (const column of columns) {
            if (
                column in jsonDataObj &&
                !!jsonDataObj[column] &&
                !binds.includes(jsonDataObj[column])
            ) {
                const obj = jsonDataObj[column]
                const valueOf = (obj: any) => {
                    if (obj && objectMaps && column in objectMaps && objectMaps[column] in obj) {
                        return obj[objectMaps[column]]
                    }
                    return obj
                }

                if (isArray(obj)) {
                    for (const val of obj) {
                        binds.push(valueOf(val))
                    }
                }
                else {
                    binds.push(valueOf(obj))
                }
            }
        }
        return binds
    }

    private static addDynamicBinds(
        tableBindings: { [key: string]: SqlValue[] },
        idToTableName: { [id: string]: DataType }
    ) {
        for (const id in idToTableName) {
            const table = idToTableName[id]
            let binds = tableBindings[table]
            if (!binds) {
                binds = []
                tableBindings[table] = binds
            }
            binds.push(id)
        }
        return tableBindings
    }

    private static addFkBindings(fk: FKType, rows: any[]) {
        let tableBindings: { [key: string]: SqlValue[] } = {} // keep track of table name and all of the ids
        for (const row of rows) {
            const jsonDataObj = !!row && 'jsonData' in row ? JSON.parse(row['jsonData']) : undefined
            if (!jsonDataObj) continue
            for (const table in fk) {
                if (table === 'columnExtractors') {
                    continue // skip columnExtractors
                }
                let binds = tableBindings[table]
                if (!binds) {
                    binds = []
                    tableBindings[table] = binds
                }
                const foriegnKeys: any = fk
                const columns: string[] = foriegnKeys[table]
                tableBindings[table] = SQLBuilder.fkColumnsToBinding(
                    columns,
                    jsonDataObj,
                    binds,
                    fk.columnExtractors
                )
            }
        }
        return tableBindings
    }
}

export const dataTypeFromRecordType = (recordType: RecordType): DataType => {
    switch (recordType) {
        case 'adjustment':
            return 'adjustments'
        case 'allocation':
            return 'allocations'
        case 'availability':
            return 'availabilities'
        case 'badge':
            return 'badges'
        case 'conflict':
            return 'conflicts'
        case 'criteria':
            return 'criterias'
        case 'dashboard':
            return 'dashboards'
        case 'feed':
            return 'feeds'
        case 'feature':
            return 'features'
        case 'link':
            return 'links'
        case 'reader':
            return 'readers'
        case 'location':
            return 'locations'
        case 'report':
            return 'reports'
        case 'review':
            return 'reviews'
        case 'subaccount':
            return 'subaccounts'
        case 'tcr':
            return 'tcrs'
        case 'goal':
            return 'goals'
        case 'contact':
            return 'contacts'
        case 'client':
            return 'clients'
        case 'category':
            return 'categories'
        case 'team':
            return 'teams'
        case 'user':
            return 'users'
        case 'event':
            return 'events'
        case 'note':
            return 'notes'
        case 'booking':
            return 'bookings'
        case 'item':
            return 'items'
        case 'field':
            return 'fields'
        case 'contract':
            return 'contracts'
        case 'payee':
            return 'payees'
        case 'device':
            return 'devices'
        case 'type':
            return 'types'
        case 'subscription':
            return 'subscriptions'
        case 'signature':
            return 'signatures'
        case 'task':
            return 'tasks'
        case 'estimate':
            return 'estimates'
        case 'gift':
            return 'gifts'
        case 'industry':
            return 'industries'
        case 'trigger':
            return 'triggers'
        case 'template':
            return 'templates'
        case 'source':
            return 'sources'
        case 'invoice':
            return 'invoices'
        case 'balance':
            return 'balances'
        case 'payment':
            return 'payments'
        case 'charge':
            return 'charges'
        case 'deposit':
            return 'deposits'
        case 'number':
            return 'numbers'
        case 'order':
            return 'orders'
        case 'credential':
            return 'credentials'
        case 'schedule':
            return 'schedules'
        case 'job':
            return 'jobs'
        case 'promotion':
            return 'promotions'
        case 'taxcode':
            return 'taxcodes'
        case 'record':
            return 'records'
        case 'channel':
            return 'channels'
        case 'line':
            return 'lines'
        case 'image':
            return 'images'
        case 'lesson':
            return 'lessons'
        case 'edition':
            return 'editions'
        case 'option':
            return 'options'
        case 'package':
            return 'packages'
        case 'component':
            return 'components'
        case 'conference':
            return 'events'
        case 'subscriber':
            return 'subscribers'
        case 'block':
            return 'blocks'
        case 'calendar':
            return 'calendars'
        case 'campaign':
            return 'campaigns'
        case 'royalty':
            return 'royalties'
        case 'change':
            return 'changes'
        case 'member':
            return 'members'
        case 'message':
            return 'messages'
        case 'requirement':
            return 'requirements'
        case 'resource':
            return 'resources'
    }
}
